/**
 * Guest Check-in Preview Parameter Composer
 *
 * Utility for building consistent preview parameters for Guest Check-in widget iframe.
 * Handles both template previews (with mock data) and form editor previews (with real data).
 */

import {
  buildWidgetIframeSrc as buildBaseWidgetIframeSrc,
  sendSettingsToWidget,
  sendPageChangeToWidget,
  sendAutoFillTestData,
  createBaseWidgetSettings,
  applyUserCustomizations,
} from '@/js/widget/widget-preview.js';

/**
 * Compose preview parameters for the Guest Check-in widget
 * @param {Object} options - Configuration options
 * @param {Object} options.form - The form object (for editor previews)
 * @param {Object} options.template - The template object (for template card previews)
 * @param {Object} options.theme - Theme configuration
 * @param {string} options.organization_name - Organization name
 * @param {string} options.background_image_url - Background image URL
 * @param {string} options.side_image_url - Side image URL
 * @param {string} options.logo_url - Logo URL
 * @param {boolean} options.use_logo - Whether to show logo
 * @param {Object} options.user_consent_settings - User consent settings
 * @param {Object} options.comp_custom_fonts - Custom fonts data
 * @param {boolean} options.is_test_submission - Whether this is a test submission
 * @param {string} options.preview_mode - Preview mode ('tablet-landscape', 'tablet', 'mobile')
 * @param {boolean} options.is_template_preview - Whether this is a template preview (vs form editor)
 * @returns {Object} Composed parameters for the widget
 */
export function composeGuestCheckInPreviewParams(options = {}) {
  const {
    form,
    template,
    theme,
    organization_name,
    background_image_url,
    side_image_url,
    logo_url,
    use_logo = false,
    user_consent_settings = {},
    comp_custom_fonts = null,
    is_test_submission = true,
    preview_mode = 'tablet-landscape',
    is_template_preview = false,
  } = options;

  let form_data;
  if (is_template_preview && template) {
    form_data = _composeTemplatePreviewForm(template, {
      theme,
      organization_name,
      background_image_url,
      side_image_url,
      logo_url,
      use_logo,
      user_consent_settings,
      comp_custom_fonts,
    });
  } else if (form) {
    // Form editor preview: use actual form data
    form_data = form;
    //console.log(form);
  } else {
    throw new Error('Either form or template must be provided');
  }

  return createBaseWidgetSettings({
    form_id_key: 'guest_check_in_form_id',
    form_id: form?.guest_check_in_form_id,
    form: form_data,
    is_test_submission,
    is_template_preview,
    preview_mode,
  });
}

/**
 * Compose a mock form object for template previews
 * @param {Object} template - Template configuration
 * @param {Object} context - Additional context (theme, images, etc.)
 * @returns {Object} Mock form object
 */
function _composeTemplatePreviewForm(template, context = {}) {
  const {
    theme,
    organization_name,
    background_image_url,
    side_image_url,
    logo_url,
    use_logo,
    user_consent_settings,
    comp_custom_fonts,
  } = context;

  // Base form structure with defaults
  const base_form = {
    guest_check_in_form_id: `preview-${Date.now()}`,
    internal_label: 'Preview Form',
    organization_name: organization_name || 'Sample Organization',
    organization_id: 'preview-org',

    // Logo configuration
    use_logo: use_logo,
    logo_image: logo_url ? { url: logo_url, style: '' } : null,

    // Layout and styling
    layout_mode: 'single-column',
    page_background: background_image_url
      ? { url: background_image_url, style: '' }
      : { style: '' },
    side_image: side_image_url ? { url: side_image_url, style: '' } : { style: '' },
    theme: theme || {},

    // Header content
    banner_text: { text: 'Welcome', style: '' },
    line_1: { text: 'Please check in below', style: '' },
    line_2: null,
    line_3: null,
    header_background: { style: '' },

    // Form fields with default configurations
    first_name: {
      field_mode: 'required',
      text: 'First Name',
      style: '',
      mobile_styles: '',
    },
    last_name: {
      field_mode: 'required',
      text: 'Last Name',
      style: '',
      mobile_styles: '',
    },
    birthday: {
      field_mode: 'optional',
      text: 'Date of Birth',
      style: '',
      mobile_styles: '',
    },
    email_address: {
      field_mode: 'optional',
      text: 'Email Address',
      style: '',
      mobile_styles: '',
    },
    phone_number: {
      field_mode: 'optional',
      text: 'Phone Number',
      style: '',
      mobile_styles: '',
    },

    // Consent settings - use values from user_consent_settings if provided
    email_consent_gathering_state:
      user_consent_settings?.email_consent_gathering_state || 'checkbox-consent',
    email_consent_agreement_type: 'default-agreement',
    email_consent_agreement_custom_patterns: [],
    phone_number_consent_gathering_state:
      user_consent_settings?.phone_consent_gathering_state ||
      user_consent_settings?.phone_number_consent_gathering_state ||
      'checkbox-consent',
    phone_number_consent_agreement_type: 'default-agreement',
    phone_number_consent_agreement_custom_patterns: [],

    // Agreement styles
    email_agreement_style: {
      style: 'font-family: Arial, sans-serif; font-size: 12px; font-style: italic;',
    },
    phone_number_agreement_style: {
      style: 'font-family: Arial, sans-serif; font-size: 12px; font-style: italic;',
    },

    // Submit button
    send_button: {
      style: '',
      mobile_styles: '',
    },
    send_button_text: {
      text: 'Check In',
      style: '',
      mobile_styles: '',
    },

    // Age gate
    age_gate_min_age_in_years: 21,

    // Thank you page
    thank_you_line_1: {
      text: 'Thank You!',
      style: '',
      mobile_styles: '',
    },
    thank_you_line_2: {
      text: 'Your information has been submitted successfully.',
      style: '',
      mobile_styles: '',
    },
    check_in_success_icon: {
      text: 'mdi-check-circle',
      style: 'color: #28a745; font-size: 48px;',
      mobile_styles: '',
    },

    // Reset timer (disabled for preview)
    reset_timer_seconds: 0,

    // Spinner styling
    spinner: { style: 'color: #007bff;' },

    // Container styling
    container: { style: '', mobile_styles: '' },
    container_background: { style: '', mobile_styles: '' },
    container_content: { style: '', mobile_styles: '' },
    background_blur: { style: '', mobile_styles: '' },
  };

  // Get template overrides from the template object
  const template_overrides = template.template_overrides || template;

  // Merge template overrides into base form
  const merged_form = {
    ...base_form,
    ...template_overrides,
  };

  return applyUserCustomizations(merged_form, {
    background_image_url,
    side_image_url,
    logo_url,
    use_logo,
    comp_custom_fonts,
    theme,
  });
}

/**
 * Build guest check-in widget iframe source URL
 * @param {string} preview_mode - Preview mode ('tablet-landscape', 'tablet', 'mobile')
 * @param {boolean} is_development - Whether in development mode
 * @returns {string} Widget iframe source URL
 */
export function buildWidgetIframeSrc(preview_mode = 'tablet-landscape', is_development = false) {
  return buildBaseWidgetIframeSrc({
    widget_name: 'guest-check-in',
    preview_mode,
    is_development,
    dev_port: 5175,
  });
}

export { sendSettingsToWidget, sendPageChangeToWidget, sendAutoFillTestData };
